/**
 * Head Guardian
 *
 * Runs once at app boot and watches `document.head` for tags injected
 * AFTER the initial static index.html. Its job is to keep the rendered
 * head clean regardless of what the external Valentine Digital / OTTO
 * loader emits.
 *
 * Responsibilities:
 *  1. Mark every dynamically-injected SEO tag with
 *     `data-dynamic-seo="true"` (unless the node opts out with
 *     `data-static="true"`), so the rest of the app and tests can
 *     distinguish baseline tags from injected ones.
 *  2. Enforce **exactly one** `<link rel="canonical">` per route — keep
 *     the first, remove subsequent duplicates.
 *  3. Strip ALL injected SEO tags when
 *     `document.documentElement.dataset.routeStatus === "404"`:
 *     canonical, robots index/follow, description, og:*, twitter:*,
 *     JSON-LD.
 *  4. Consolidate JSON-LD: keep at most one entity per `@type` for
 *     `LocalBusiness`, `Organization`, `HomeAndConstructionBusiness`
 *     (the common business-entity schemas the external loader
 *     re-emits on every nav).
 *  5. Cap standalone `ImageObject` JSON-LD blocks to MAX_IMAGE_OBJECTS.
 *  6. Remove any JSON-LD that fails to parse or whose `@type` is the
 *     literal string `"UNKNOWN"`.
 */

const CONFLICT_404_SELECTOR = [
  'link[rel="canonical"]',
  'meta[name="description"]',
  'meta[name="robots"]',
  'meta[name="googlebot"]',
  'meta[property^="og:"]',
  'meta[name^="og:"]',
  'meta[name^="twitter:"]',
  'script[type="application/ld+json"]',
].join(", ");

const BUSINESS_ENTITY_TYPES = new Set([
  "LocalBusiness",
  "Organization",
  "HomeAndConstructionBusiness",
  "PaintingService",
  "ProfessionalService",
]);

// Routes that must never carry business / service / FAQ schema.
// SearchAtlas/OTTO and any leftover prerender or React emitter is
// stripped here regardless of source.
const UTILITY_PATH_PREFIXES = ["/portal", "/admin", "/auth", "/unsubscribe", "/qr"];
const UTILITY_STRIP_TYPES = new Set([
  "LocalBusiness",
  "Organization",
  "HomeAndConstructionBusiness",
  "PaintingService",
  "ProfessionalService",
  "Service",
  "FAQPage",
  "AggregateRating",
]);

function isUtilityPath(): boolean {
  const p = window.location.pathname.toLowerCase();
  return UTILITY_PATH_PREFIXES.some((pre) => p === pre || p.startsWith(pre + "/"));
}

const MAX_IMAGE_OBJECTS = 3;

const NOT_FOUND_TITLE = "404 — Page Not Found | Thumb Premier Painting";

let started = false;

function is404(): boolean {
  return document.documentElement.dataset.routeStatus === "404";
}

function isSeoNode(node: Element): boolean {
  return (
    node.tagName === "TITLE" ||
    (node.matches?.(CONFLICT_404_SELECTOR) ?? false)
  );
}

function tagDynamic(node: Element): void {
  if (node.getAttribute("data-static") === "true") return;
  if (node.getAttribute("data-not-found-seo") === "true") return;
  if (!node.hasAttribute("data-dynamic-seo")) {
    node.setAttribute("data-dynamic-seo", "true");
  }
}

/** Strip trailing slash (except root), strip hash + query, lowercase host. */
export function normalizeUrl(href: string): string {
  try {
    const u = new URL(href, window.location.origin);
    u.hash = "";
    u.search = "";
    let path = u.pathname;
    if (path.length > 1 && path.endsWith("/")) path = path.replace(/\/+$/, "");
    return `${u.protocol}//${u.host.toLowerCase()}${path}`;
  } catch {
    return href;
  }
}

/** Pathname-only normalization for cross-host canonical matching. */
function normalizePath(href: string): string {
  try {
    const u = new URL(href, window.location.origin);
    let p = u.pathname || "/";
    if (p.length > 1 && p.endsWith("/")) p = p.replace(/\/+$/, "");
    return p;
  } catch {
    return href;
  }
}

function currentNormalizedPath(): string {
  return normalizePath(window.location.pathname);
}

function dedupeCanonical(): void {
  const links = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'),
  );
  if (links.length <= 1) return;
  const currentPath = currentNormalizedPath();
  // Prefer the canonical whose normalized pathname matches the current route.
  // Compare pathnames only so the canonical wins regardless of host
  // (preview domain vs production domain vs www).
  const match = links.find((l) => normalizePath(l.href) === currentPath);
  const keep = match ?? links[0];
  for (const l of links) if (l !== keep) l.remove();
}

/**
 * Dedupe meta tags that identify by `name` or `property`. We always
 * keep the LAST instance — Helmet writes per-route tags after the
 * static index.html fallbacks, and SearchAtlas/OTTO (when active)
 * writes after Helmet. Last-write-wins matches user intent at every
 * layer and prevents stacked og:/twitter:/description tags.
 */
const DEDUPE_META_KEYS: Array<["name" | "property", string[]]> = [
  ["name", ["description", "robots", "googlebot", "twitter:card", "twitter:title", "twitter:description", "twitter:image", "twitter:url"]],
  ["property", ["og:title", "og:description", "og:url", "og:type", "og:image", "og:site_name"]],
];

function dedupeMeta(): void {
  for (const [attr, keys] of DEDUPE_META_KEYS) {
    for (const key of keys) {
      const nodes = Array.from(
        document.head.querySelectorAll<HTMLMetaElement>(`meta[${attr}="${key}"]`),
      );
      if (nodes.length <= 1) continue;
      const keep = nodes[nodes.length - 1];
      for (const n of nodes) if (n !== keep) n.remove();
    }
  }
}

/**
 * Keep exactly one <title>. Prefer a non-static, non-empty title (the
 * route-specific one injected after hydration). Fall back to the static
 * index.html title when nothing else is available.
 */
function dedupeTitle(): void {
  const titles = Array.from(document.head.querySelectorAll<HTMLTitleElement>("title"));
  if (titles.length <= 1) return;
  const dynamicWithText = titles.filter(
    (t) => t.getAttribute("data-static") !== "true" && (t.textContent?.trim().length ?? 0) > 0,
  );
  const keep = dynamicWithText[dynamicWithText.length - 1] ?? titles[0];
  for (const t of titles) if (t !== keep) t.remove();
}

type LdNode = { el: HTMLScriptElement; data: any; type: string | null };

function readLd(el: HTMLScriptElement): LdNode | null {
  const raw = el.textContent?.trim();
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    const type = Array.isArray(data?.["@type"]) ? data["@type"][0] : data?.["@type"];
    return { el, data, type: typeof type === "string" ? type : null };
  } catch {
    return null;
  }
}

function consolidateJsonLd(): void {
  // Inspect ld+json everywhere on the page — SearchAtlas/OTTO sometimes
  // injects schema blocks into <body> instead of <head>, and those still
  // count toward "rendered DOM" audits.
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>(
      'script[type="application/ld+json"]',
    ),
  );

  const seenBusiness = new Set<string>();
  const seenIds = new Set<string>();
  const seenServiceUrls = new Set<string>();
  // Single-instance schema types: any page should render at most one
  // of each. Prerender + Helmet sometimes both emit a Service /
  // FAQPage / BreadcrumbList for the same route; keep the first.
  const SINGLETON_TYPES = new Set(["Service", "FAQPage", "BreadcrumbList"]);
  const seenSingleton = new Set<string>();
  const utility = isUtilityPath();
  let imageObjectCount = 0;

  for (const el of scripts) {
    const parsed = readLd(el);

    // Invalid JSON → remove.
    if (!parsed) { el.remove(); continue; }

    const { type } = parsed;

    // UNKNOWN / missing @type → remove.
    if (!type || type === "UNKNOWN") { el.remove(); continue; }

    // Strip standalone AggregateRating blocks. We do NOT emit verified
    // AggregateRating from Lovable source — anything matching this type
    // is injected by SearchAtlas/OTTO with templated values
    // (e.g. ratingValue "5", reviewCount "100+"). Until we have a
    // verified-review pipeline, all AggregateRating is removed.
    if (type === "AggregateRating") { el.remove(); continue; }

    // Strip standalone Review / Rating blocks for the same reason —
    // we only emit verified review markup on the /reviews and
    // /service-quality-trust pages. Anywhere else, a Review/Rating
    // node is third-party injection (e.g. "Local Homeowner", rating 5).
    if (type === "Review" || type === "Rating") {
      const path = window.location.pathname.toLowerCase();
      const allowed = path === "/reviews" || path.startsWith("/reviews/") || path.startsWith("/service-quality-trust");
      if (!allowed) { el.remove(); continue; }
    }

    // Strip nested `aggregateRating` from any node that carries one.
    // Same reasoning — none of our schema generators emit this property,
    // so its presence indicates third-party injection.
    if (stripNestedAggregateRating(parsed.data)) {
      try {
        el.textContent = JSON.stringify(parsed.data);
      } catch { /* leave as-is if re-serialization fails */ }
    }

    // Utility pages (/portal, /admin, /auth, /unsubscribe, /qr) must
    // not carry business / service / FAQ schema from any source.
    if (utility && UTILITY_STRIP_TYPES.has(type)) {
      el.remove();
      continue;
    }

    // Dedupe any node carrying an @id we've already seen this page.
    const idVal = (parsed.data as any)?.["@id"];
    if (typeof idVal === "string" && idVal) {
      if (seenIds.has(idVal)) { el.remove(); continue; }
      seenIds.add(idVal);
    }

    // Dedupe Service by url (prerender + React both emit one per page).
    if (type === "Service") {
      const u = (parsed.data as any)?.url || idVal || "";
      if (u) {
        if (seenServiceUrls.has(u)) { el.remove(); continue; }
        seenServiceUrls.add(u);
      }
    }

    // Enforce max-1 per page for Service / FAQPage / BreadcrumbList.
    // This catches the case where prerender and React Helmet both
    // emit a Service or FAQPage on the same route without a shared
    // @id or url to dedupe against (e.g. /shake-roof-restoration).
    if (SINGLETON_TYPES.has(type)) {
      if (seenSingleton.has(type)) { el.remove(); continue; }
      seenSingleton.add(type);
    }

    // Dedupe core business entities.
    if (BUSINESS_ENTITY_TYPES.has(type)) {
      if (seenBusiness.has(type)) { el.remove(); continue; }
      seenBusiness.add(type);
      continue;
    }

    // Cap standalone ImageObject blocks.
    if (type === "ImageObject") {
      imageObjectCount++;
      if (imageObjectCount > MAX_IMAGE_OBJECTS) { el.remove(); continue; }
    }
  }
}

/**
 * Walk a JSON-LD object/array and delete any unverified rating/review
 * properties — `aggregateRating`, `review`, `reviews`, `reviewRating`,
 * `ratingValue`, `reviewCount`, `ratingCount`. We do not emit any of
 * these from Lovable source on indexable marketing pages, so their
 * presence indicates third-party (SearchAtlas/OTTO) injection of
 * templated review markup (e.g. "Local Homeowner", ratingValue 5).
 * Returns true if at least one property was removed so the caller
 * knows to re-serialize the <script> body.
 */
const STRIP_RATING_KEYS = new Set([
  "aggregateRating",
  "review",
  "reviews",
  "reviewRating",
  "ratingValue",
  "reviewCount",
  "ratingCount",
]);

function stripNestedAggregateRating(node: unknown): boolean {
  if (!node || typeof node !== "object") return false;
  let touched = false;
  if (Array.isArray(node)) {
    for (const item of node) if (stripNestedAggregateRating(item)) touched = true;
    return touched;
  }
  const obj = node as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (STRIP_RATING_KEYS.has(key)) {
      delete obj[key];
      touched = true;
      continue;
    }
    if (stripNestedAggregateRating(obj[key])) touched = true;
  }
  return touched;
}

function strip404(): void {
  // Remove all SEO conflict tags that aren't the noindex robots we own.
  document.head
    .querySelectorAll(CONFLICT_404_SELECTOR)
    .forEach((n) => {
      if (n.getAttribute("data-not-found-seo") === "true") return;
      n.remove();
    });

  // Defensive: if an index/follow robots somehow has data-not-found-seo,
  // drop it. Only `noindex` content is allowed to remain.
  document.head
    .querySelectorAll('meta[name="robots"]')
    .forEach((n) => {
      const c = (n.getAttribute("content") || "").toLowerCase();
      if (!c.includes("noindex")) n.remove();
    });

  // Ensure exactly one title with the canonical 404 string.
  const titles = Array.from(document.head.querySelectorAll<HTMLTitleElement>("title"));
  for (let i = 1; i < titles.length; i++) titles[i].remove();
  if (titles[0]) {
    if (titles[0].textContent !== NOT_FOUND_TITLE) {
      titles[0].textContent = NOT_FOUND_TITLE;
    }
  } else {
    const t = document.createElement("title");
    t.setAttribute("data-not-found-seo", "true");
    t.textContent = NOT_FOUND_TITLE;
    document.head.appendChild(t);
  }
}

function sweep(): void {
  if (is404()) {
    strip404();
    return;
  }
  dedupeTitle();
  dedupeCanonical();
  dedupeMeta();
  consolidateJsonLd();
}

export function startHeadGuardian(): void {
  if (started || typeof document === "undefined") return;
  started = true;

  // Tag every existing SEO node that wasn't part of the static
  // index.html as dynamic-seo. We treat anything already present at
  // boot as "baseline" (static) unless it lacks data-static and is a
  // known SEO node — leave the file-shipped tags alone.
  // (We intentionally do NOT auto-tag pre-existing nodes; only future
  // additions are tagged so we don't false-positive on index.html.)

  const observer = new MutationObserver((mutations) => {
    let touched = false;
    for (const m of mutations) {
      for (const n of Array.from(m.addedNodes)) {
        if (!(n instanceof Element)) continue;
        if (!isSeoNode(n)) continue;
        touched = true;
        tagDynamic(n);
      }
    }
    if (touched) sweep();
  });
  observer.observe(document.head, { childList: true, subtree: false });
  // SearchAtlas/OTTO occasionally injects ld+json scripts into <body>
  // (e.g. nested AggregateRating). Observe body subtree so those get
  // swept as well.
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener("DOMContentLoaded", () => {
      observer.observe(document.body, { childList: true, subtree: true });
      sweep();
    }, { once: true });
  }

  // Also sweep on route-status changes (NotFound emits this on unmount).
  window.addEventListener("route-status-change", () => sweep());

  // Initial sweep in case static head already has redundant nodes.
  sweep();
}